import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogEmphasis,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import type { FirmDocumentAnalyzeDialogProps } from './FirmDocumentAnalyzeDialog.props';
import {
  FirmDocumentDialogDocumentListItem,
  FirmDocumentDialogScrollableDocumentList,
  FirmDocumentDialogSupportingText,
} from './FirmDocumentFileActionDialogs.styles';
import { FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS } from './FirmDocumentFileActionDialogs.testIds';
import { getFirmDocumentDialogFileNameItems } from './FirmDocumentFileActionDialogs.utilities';

type AnalyzeDialogContentState = Pick<
  FirmDocumentAnalyzeDialogProps,
  'actionLabel' | 'documentCount' | 'fileNames'
>;

const getAnalyzeTitle = (actionLabel: string) => `Start ${actionLabel}`;

const renderAnalyzeDocumentList = (fileNames?: string[]) => {
  if (!fileNames?.length) {
    return null;
  }

  return (
    <FirmDocumentDialogScrollableDocumentList
      data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.analyzeDocumentList}>
      {getFirmDocumentDialogFileNameItems(fileNames).map(({ fileName, key }) => (
        <FirmDocumentDialogDocumentListItem key={key}>
          {fileName}
        </FirmDocumentDialogDocumentListItem>
      ))}
    </FirmDocumentDialogScrollableDocumentList>
  );
};

const FirmDocumentAnalyzeDialog = ({
  actionLabel,
  documentCount,
  fileNames,
  isAnalyzing = false,
  onClose,
  onConfirm,
  open,
}: FirmDocumentAnalyzeDialogProps) => {
  const [visibleContent, setVisibleContent] = useState<AnalyzeDialogContentState>({
    actionLabel,
    documentCount,
    fileNames,
  });

  // Preserve the previous dialog copy while the close animation runs with empty selection state.
  useEffect(() => {
    if (open && documentCount > 0) {
      setVisibleContent({ actionLabel, documentCount, fileNames });
    }
  }, [actionLabel, documentCount, fileNames, open]);

  const dialogContent = open && documentCount > 0
    ? { actionLabel, documentCount, fileNames }
    : visibleContent;

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.analyzeDialog}
      maxWidth="sm"
      onClose={isAnalyzing ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>{getAnalyzeTitle(dialogContent.actionLabel)}</DialogTitle>
        <DialogCloseButton
          aria-label="Close analyze documents dialog"
          data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.analyzeCloseButton}
          disabled={isAnalyzing}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>
          <span>
            Start <DialogEmphasis>{dialogContent.actionLabel}</DialogEmphasis>
            {` for ${dialogContent.documentCount} selected ${
              dialogContent.documentCount === 1 ? 'document' : 'documents'
            }?`}
          </span>
        </DialogDescription>
        {renderAnalyzeDocumentList(dialogContent.fileNames)}
        <FirmDocumentDialogSupportingText>
          This starts an AI document processing job. Confirm only when the selected files are correct.
        </FirmDocumentDialogSupportingText>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.analyzeConfirmButton}
        actionLabel="Start Analysis"
        cancelButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.analyzeCancelButton}
        isActionLoading={isAnalyzing}
        isCancelDisabled={isAnalyzing}
        onAction={onConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentAnalyzeDialog;
